/**
 * TanStack Query hooks for Estimated Hours Lines — the planner-built outline a Resource
 * Plan's estimate is made of (src/api/estimated-hours-lines.ts).
 *
 * There is no read hook here and no query key of its own: the outline rides along on
 * `ResourcePlanDetailView.estimatedHoursLines`, so every mutation below invalidates
 * `resourcePlanKeys.all` (via `useInvalidatePlans`) and returns the whole re-shaped plan,
 * same as every Workstream / Plan Line mutation in use-resource-plans.ts.
 */

import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { send, useInvalidatePlans } from "@/hooks/use-resource-plans";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

export interface CreateEstimatedHoursLineInput {
  name: string;
  hours?: string | null;
  parentLineId?: string | null;
  workstreamId?: string | null;
}

export interface UpdateEstimatedHoursLineInput {
  name?: string;
  hours?: string | null;
  included?: boolean;
  workstreamId?: string | null;
}

/** One entry of a restructure request — the whole outline's new shape, in one call. */
export interface EstimatedHoursLineStructureEntry {
  id: string;
  parentLineId: string | null;
  position: number;
}

export function useCreateEstimatedHoursLine(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: CreateEstimatedHoursLineInput) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/estimated-hours-lines`, "POST", input),
    onSuccess: () => {
      invalidate();
      toast.success("Line added");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to add the line"),
  });
}

export function useUpdateEstimatedHoursLine() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & UpdateEstimatedHoursLineInput) =>
      send<ResourcePlanDetailView>(`/api/estimated-hours-lines/${id}`, "PATCH", patch),
    // Deliberately silent on success: this fires on every blur-commit and checkbox toggle,
    // so a toast here would be one per keystroke-commit — noise, not signal. Errors still
    // surface below.
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the line"),
  });
}

export function useDeleteEstimatedHoursLine() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (id: string) =>
      send<ResourcePlanDetailView>(`/api/estimated-hours-lines/${id}`, "DELETE"),
    onSuccess: () => {
      invalidate();
      toast.success("Line removed");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to remove the line"),
  });
}

/** Drag/indent/outdent/move — the client computes the new tree with the same pure outline
 * ops the server validates against (src/lib/estimated-hours-lines.ts), then posts it whole. */
export function useRestructureEstimatedHoursLines(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (lines: EstimatedHoursLineStructureEntry[]) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/estimated-hours-lines/structure`, "PUT", {
        lines,
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Outline updated");
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to reorder the lines"),
  });
}
